// The same rules the backend applies (backend/services/databaseServers.js,
// backend/config/databaseNames.js), so the forms can say what to fix before
// anything is sent.
export const ADDRESS_PATTERN = /^(neo4j|bolt)(\+s|\+ssc)?:\/\/[A-Za-z0-9.-]+(:\d{1,5})?\/?$/i;
// Mirrors backend/config/databaseNames.js exactly, including the "system"
// prefix the server also refuses.
export const NAME_PATTERN = /^(?!system)[a-z](?:[a-z0-9]|[.-](?=[a-z0-9])){2,62}$/;
export const NAME_RULE =
  "3 to 63 letters, digits, dots or dashes, starting with a letter, ending with a letter or digit, with no two dots or dashes in a row";

export const normaliseName = (value) => String(value || "").trim().toLowerCase();

// ── AuraDB needs TLS ───────────────────────────────────────────────────────
// Typed as neo4j://abc123.databases.neo4j.io, AuraDB answers with the driver's
// routing dump ("No routing servers available. Known routing table: ...") and
// an admin is none the wiser. The address alone says what is wrong, so the
// form says it before a test is spent. Mirrors databaseRegistry.connectionAdvice.
const addressHost = (value) =>
  String(value || "")
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "")
    .replace(/[/?#].*$/, "")
    .replace(/^[^@]*@/, "");

const hostName = (value) => addressHost(value).replace(/:\d+$/, "");

// AuraDB instances are *.databases.neo4j.io; anything under neo4j.io is treated the same.
export const isAuraAddress = (value) => /(^|\.)neo4j\.io$/i.test(hostName(value));

export const isEncryptedAddress = (value) => /^(neo4j|bolt)\+(s|ssc):\/\//i.test(String(value || "").trim());

// The same address, encrypted. The port and host are kept as typed.
export const secureAddress = (value) => `neo4j+s://${addressHost(value)}`;

// What the form says about the address, or null when there is nothing to say.
export const encryptionHint = (value) => {
  const uri = String(value || "").trim();
  if (!uri || !ADDRESS_PATTERN.test(uri) || !isAuraAddress(uri) || isEncryptedAddress(uri)) return null;
  return {
    message: "AuraDB needs an encrypted connection.",
    next: `Use ${secureAddress(uri)} - the same address, over TLS.`,
    fix: secureAddress(uri),
    fixLabel: "Use neo4j+s://",
  };
};

const slug = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30);

// The key a database gets in links. A main-server database keeps its name;
// "neo4j" on another server is prefixed with the server, since every server
// has one.
export const suggestKey = (server, databaseName) => {
  const name = normaliseName(databaseName);
  if (!name || !server || server.id === "main" || name !== "neo4j") return name;
  const prefixed = `${slug(server.name) || "server"}-${name}`.slice(0, 63);
  return NAME_PATTERN.test(prefixed) ? prefixed : `db-${prefixed}`.slice(0, 63);
};
